// Registers public/sw.js (image cache) and, on repeat visits, asks it to re-check
// the images this page used a little after load. See public/sw.js for the caching side.

const REVALIDATE_DELAY_MS = 12000;
const IMAGE_RE = /\.(png|jpe?g|webp|gif|svg|ico|avif)(\?|$)/i;

// images the page has requested so far (<img>, CSS backgrounds, video posters)
const usedImageUrls = () =>
  performance
    .getEntriesByType("resource")
    .map((e) => e.name)
    .filter((u) => u.startsWith(window.location.origin) && IMAGE_RE.test(u));

// show updated images without a reload: point matching <img>s at the fresh cached copy
const swapUpdatedImages = async (cacheName, paths) => {
  const cache = await caches.open(cacheName);
  await Promise.all(
    paths.map(async (path) => {
      const response = await cache.match(path);
      if (!response) return;
      const blobUrl = URL.createObjectURL(await response.blob());
      document.querySelectorAll("img").forEach((img) => {
        const src = img.currentSrc || img.src;
        if (src && new URL(src, window.location.href).pathname === path) img.src = blobUrl;
      });
    })
  );
};

export function registerImageCache() {
  // dev server rebuilds constantly; caching images there only gets in the way
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    // a controller only exists when the site was loaded before, i.e. images may be served from cache
    const isRepeatVisit = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.register("/sw.js").catch(() => {});

    if (!isRepeatVisit) return;

    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data || {};
      if (data.type === "images-updated") swapUpdatedImages(data.cache, data.paths).catch(() => {});
    });

    setTimeout(() => {
      navigator.serviceWorker.controller?.postMessage({ type: "revalidate-images", urls: usedImageUrls() });
    }, REVALIDATE_DELAY_MS);
  });
}
